import { Ionicons } from '@expo/vector-icons';
import type { ComponentProps } from 'react';
import { getTokenValue, type Token } from 'tamagui';

import { color } from '@/constants/tokens';

/** The `$name` strings for our color tokens (`$brand`, `$textDark`, …). */
type ColorToken = `$${keyof typeof color}`;

/**
 * PSIcon — token-colored icon (atom).
 *
 * `@expo/vector-icons` isn't a Tamagui component, so a `$token` string handed to
 * its `color` prop is NOT resolved — it reaches the native glyph verbatim and
 * renders wrong (see the black-icon trap we hit). This wrapper is the one place
 * that bridges the gap: it takes a `$token` color like every other component in
 * the app and resolves it to the raw value the icon needs, via `getTokenValue`.
 *
 * We resolve at build/render call rather than via `useTheme` on purpose — the
 * app has a single `ps` theme (no light/dark), so a token always maps to one
 * value and a runtime theme subscription would buy nothing.
 *
 * `size` is a raw number (icon size isn't one of our token categories, and the
 * lint rule doesn't police it); defaults to PSCircleButton's typical glyph size.
 */
type PSIconProps = {
  name: ComponentProps<typeof Ionicons>['name'];
  /** A `$color` token name, e.g. "$textDark" (default) or "$textWhite". */
  color?: ColorToken;
  size?: number;
};

export default function PSIcon({
  name,
  color = '$textDark',
  size = 20,
}: PSIconProps) {
  // color is a ColorToken (a strict subset of Tamagui's broad Token union); the
  // cast just narrows to what getTokenValue's signature accepts.
  return (
    <Ionicons
      name={name}
      size={size}
      color={getTokenValue(color as Token, 'color')}
      // Ionicons is a Text glyph: without this, its default line height (plus
      // Android's includeFontPadding) leaves extra space below the glyph,
      // which throws off alignItems="center" next to adjacent text.
      style={{ lineHeight: size, includeFontPadding: false }}
    />
  );
}
